/**
 * How a resolver's output finds its elements again. The worker keys each
 * context and the main thread keys each element (`identify`), and a rule
 * applies where the two keys meet. Shared by both sides, so they cannot
 * drift.
 */

/**
 * Every element of the target: the key of a target resolved once, without
 * context, which has nothing per element to key by.
 */
export const EVERY_ELEMENT_KEY = "*";

/** A product in a grid, by its id. */
export function productKey(id: number | string): string {
	return `product:${id}`;
}

/**
 * The key of a product-grid card containing `element`, or `null` outside of
 * one. Core themes mark every card with `data-product-type="list"` and the
 * product's id, a hook the product page and the quick shop never carry.
 */
export function gridProductKey(element: Element): string | null {
	const id = element
		.closest('[data-product-type="list"]')
		?.getAttribute("data-product-id");
	return id ? productKey(id) : null;
}

/**
 * The key of the product an add-to-cart `element` adds, or `null` outside of
 * a form that names one. Every Core theme posts the product's id to the cart
 * as `add_to_cart` — on the product page, on grid cards and in the quick shop
 * alike — and its own scripts read the id back from there, so the hook
 * survives where the card markup does not (the quick shop moves the form out
 * of its card).
 */
export function formProductKey(element: Element): string | null {
	const input = element
		.closest("form")
		?.querySelector('input[name="add_to_cart"]');
	const id = input instanceof HTMLInputElement ? input.value.trim() : "";
	return id ? productKey(id) : null;
}

/** A cart line item, by its id: `CartItem.id`, not the product's. */
export function cartItemKey(id: number | string): string {
	return `cart-item:${id}`;
}

/**
 * The key of the cart line item containing `element`, or `null` outside of
 * one. Every Core theme roots each line item, in the cart drawer and on the
 * cart page alike, at a `.js-cart-item` carrying `data-item-id`, the same id
 * the cart state reports for that item (Core's own cart scripts look items up
 * by it).
 */
export function cartLineItemKey(element: Element): string | null {
	const id = element
		.closest(".js-cart-item")
		?.getAttribute("data-item-id")
		?.trim();
	return id ? cartItemKey(id) : null;
}
